import { cn } from "@/lib/cn";

interface AvatarProps {
  name: string;
  size?: "sm" | "md" | "lg";
  /** Optional profile image. Falls back to coloured initials when absent. */
  src?: string | null;
  className?: string;
}

const sizes = {
  sm: "h-6 w-6 text-[10px]",
  md: "h-8 w-8 text-xs",
  lg: "h-10 w-10 text-sm",
};

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]!.toUpperCase())
    .join("");
}

// Stable hue per name so the same person always gets the same avatar color.
function hueFromName(name: string) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (hash * 31 + name.charCodeAt(i)) | 0;
  }
  return Math.abs(hash) % 360;
}

export function Avatar({ name, size = "md", src, className }: AvatarProps) {
  if (src) {
    return (
      <img
        src={src}
        alt=""
        className={cn(
          "inline-block shrink-0 rounded-full object-cover",
          sizes[size],
          className,
        )}
        aria-hidden="true"
      />
    );
  }

  const hue = hueFromName(name);
  return (
    <span
      className={cn(
        "inline-flex items-center justify-center rounded-full font-medium text-white shrink-0",
        sizes[size],
        className,
      )}
      style={{ background: `oklch(0.62 0.14 ${hue})` }}
      aria-hidden="true"
    >
      {initials(name)}
    </span>
  );
}
